"use client";

import { useRouter } from "next/navigation";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";
const URB = "var(--font-urbanist), sans-serif";

export type LoginMethod = "wallet" | "gmail" | "facebook" | "apple" | "guest";

interface LoginModalProps {
  onSelect?: (method: LoginMethod) => void;
  onClose?: () => void;
}

export default function LoginModal({ onSelect, onClose }: LoginModalProps = {}) {
  const router = useRouter();
  const handleClose = () => {
    if (onClose) onClose();
    else router.push("/app");
  };
  const handleSelect = (method: LoginMethod) => {
    if (onSelect) onSelect(method);
    else router.push("/app");
  };

  return (
    <div
      className="app-anim-scale-in desktop-glass-modal"
      style={{
        width: 460,
        maxWidth: 460,
        background: "#1E1E1E",
        border: "1px solid rgba(242,240,237,0.1)",
        borderRadius: 24,
        padding: 33,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        boxShadow: "0px 32px 64px -12px rgba(0,0,0,0.8)",
      }}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        aria-label="Close"
        className="app-close-btn"
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          width: 42.8,
          height: 42.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <span style={{ display: "flex", transform: "scale(0.95)" }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              padding: 12,
              borderRadius: 9999,
            }}
          >
            <img
              src="/app-icons/multiply.svg"
              alt=""
              style={{ width: 20, height: 20 }}
            />
          </span>
        </span>
      </button>

      {/* Logo */}
      <div style={{ padding: 8.571 }}>
        <img
          src="/app-icons/logo-mark.svg"
          alt="BotWork"
          style={{ width: 42.857, height: 42.857 }}
        />
      </div>

      {/* Header */}
      <div
        style={{
          paddingBottom: 40,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "center",
        }}
      >
        <h2
          style={{
            fontFamily: PJK,
            fontWeight: 700,
            fontSize: 24,
            lineHeight: "32px",
            color: "#F2F0ED",
            margin: 0,
            textAlign: "center",
          }}
        >
          Connect to BotWork
        </h2>
        <p
          style={{
            fontFamily: URB,
            fontWeight: 600,
            fontSize: 14,
            lineHeight: "20px",
            color: "rgba(242,240,237,0.5)",
            margin: 0,
            textAlign: "center",
          }}
        >
          Sign in to access the P2P agent marketplace
        </p>
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        {/* Connect Wallet (primary) */}
        <button
          onClick={() => handleSelect("wallet")}
          className="app-btn-primary"
          style={{
            background: "#8635F6",
            borderRadius: 9999,
            width: "100%",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            border: "none",
            cursor: "pointer",
          }}
        >
          <img
            src="/app-icons/wallet.svg"
            alt=""
            style={{ width: 19, height: 18 }}
          />
          <span
            style={{
              fontFamily: URB,
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "24px",
              color: "#F2F0ED",
              textAlign: "center",
            }}
          >
            Connect Wallet
          </span>
        </button>

        {/* Gmail */}
        <SocialButton icon="gmail.svg" iconWidth={20} iconHeight={16} onClick={() => handleSelect("gmail")}>
          Continue with Gmail
        </SocialButton>

        {/* Facebook */}
        <SocialButton icon="facebook.svg" iconWidth={20} iconHeight={20} onClick={() => handleSelect("facebook")}>
          Continue with Facebook
        </SocialButton>

        {/* Apple */}
        <SocialButton icon="apple.svg" iconWidth={20} iconHeight={20} onClick={() => handleSelect("apple")}>
          Continue with Apple
        </SocialButton>
      </div>

      {/* Divider */}
      <div
        style={{
          paddingTop: 32,
          paddingBottom: 32,
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div style={{ flex: 1, height: 1, background: "rgba(242,240,237,0.1)" }} />
        <span
          style={{
            fontFamily: PJK,
            fontWeight: 400,
            fontSize: 12,
            lineHeight: "16px",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            color: "rgba(242,240,237,0.3)",
          }}
        >
          OR
        </span>
        <div style={{ flex: 1, height: 1, background: "rgba(242,240,237,0.1)" }} />
      </div>

      {/* Guest link */}
      <button
        onClick={() => handleSelect("guest")}
        className="app-guest-link"
        style={{
          paddingBottom: 32,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "color 150ms ease-out",
        }}
      >
        <span
          style={{
            fontFamily: URB,
            fontWeight: 600,
            fontSize: 14,
            lineHeight: "20px",
            color: "rgba(242,240,237,0.5)",
            textAlign: "center",
            transition: "color 150ms ease-out",
          }}
        >
          Continue as Guest
        </span>
      </button>

      {/* Footer legal */}
      <div style={{ maxWidth: 300, width: 300, textAlign: "center" }}>
        <p
          style={{
            fontFamily: URB,
            fontWeight: 400,
            fontSize: 11,
            lineHeight: "17.88px",
            color: "rgba(242,240,237,0.3)",
            margin: 0,
          }}
        >
          By connecting, you agree to our{" "}
          <a
            href="#"
            className="app-legal-link"
            style={{
              color: "rgba(242,240,237,0.5)",
              textDecoration: "underline",
              transition: "color 150ms ease-out",
            }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="#"
            className="app-legal-link"
            style={{
              color: "rgba(242,240,237,0.5)",
              textDecoration: "underline",
              transition: "color 150ms ease-out",
            }}
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

function SocialButton({
  icon,
  iconWidth,
  iconHeight,
  children,
  onClick,
}: {
  icon: string;
  iconWidth: number;
  iconHeight: number;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="app-btn-secondary app-social-btn"
      style={{
        border: "2px solid #443F4A",
        background: "transparent",
        borderRadius: 40,
        width: "100%",
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        cursor: "pointer",
      }}
    >
      <img
        src={`/app-icons/${icon}`}
        alt=""
        style={{ width: iconWidth, height: iconHeight, transition: "filter 150ms ease-out" }}
      />
      <span
        style={{
          fontFamily: URB,
          fontWeight: 700,
          fontSize: 16,
          lineHeight: "24px",
          color: "#D5BAFF",
          textAlign: "center",
        }}
      >
        {children}
      </span>
    </button>
  );
}
